import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { auth as authApi } from '@/utils/api';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('Missing reset token in URL. Use the link from your email.');
  }, [token]);

  const strength = (() => {
    if (!pw) return { score: 0, label: '', color: 'var(--border)' };
    let s = 0;
    if (pw.length >= 8) s++; if (pw.length >= 12) s++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
    if (/\d/.test(pw)) s++; if (/[^A-Za-z0-9]/.test(pw)) s++;
    const levels = [
      { score: 1, label: 'Weak', color: '#B34A3A' },
      { score: 2, label: 'Fair', color: '#D9A441' },
      { score: 3, label: 'Good', color: '#D9A441' },
      { score: 4, label: 'Strong', color: '#3C8A5A' },
      { score: 5, label: 'Excellent', color: '#3C8A5A' },
    ];
    return levels[Math.max(0, s - 1)] || levels[0];
  })();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) return setError('Password must be at least 8 characters.');
    if (pw !== confirm) return setError('Passwords do not match.');
    setSaving(true);
    try {
      await authApi.resetPassword(token, pw);
      setDone(true);
      toast.success('Password updated — redirecting to sign in…');
      setTimeout(() => navigate('/login'), 1800);
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed — the link may be expired or already used.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }} data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="card-base p-6">
          {done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#3C8A5A15' }}>
                <CheckCircle2 size={22} style={{ color: '#3C8A5A' }} />
              </div>
              <h1 className="text-xl font-black mb-1.5" style={{ color: 'var(--ink)' }}>Password updated</h1>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="w-10 h-10 rounded-btn flex items-center justify-center mb-3" style={{ background: '#E6F1F4' }}>
                <Lock size={16} style={{ color: '#006080' }} />
              </div>
              <h1 className="text-xl font-black mb-1" style={{ color: 'var(--ink)' }}>Set a new password</h1>
              <p className="text-sm mb-5" style={{ color: 'var(--muted)' }}>Pick something strong. At least 8 characters.</p>

              {!token && (
                <div className="p-3 rounded-btn mb-3 flex gap-2 items-start" style={{ background: 'rgba(179,74,58,0.1)', border: '1px solid rgba(179,74,58,0.2)' }}>
                  <AlertCircle size={14} style={{ color: '#B34A3A', marginTop: 2 }} />
                  <p className="text-sm" style={{ color: '#B34A3A' }}>Missing token in URL. Open the link from your email.</p>
                </div>
              )}

              <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>New password</label>
                  <div className="relative">
                    <input className="input-agency pr-10" type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-pw-input" />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2" tabIndex={-1}>
                      {show ? <EyeOff size={15} color="var(--muted)" /> : <Eye size={15} color="var(--muted)" />}
                    </button>
                  </div>
                  {pw && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                        <div className="h-full transition-all" style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }} />
                      </div>
                      <span className="text-xs font-semibold" style={{ color: strength.color }}>{strength.label}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Confirm password</label>
                  <input className="input-agency" type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" />
                </div>

                {error && (
                  <div className="p-2.5 rounded-btn text-sm" style={{ background: 'rgba(179,74,58,0.1)', color: '#B34A3A' }} data-testid="reset-error">{error}</div>
                )}

                <button type="submit" className="btn-primary w-full py-2.5 text-sm" disabled={saving || !token || !pw || !confirm} data-testid="reset-submit">
                  {saving ? 'Saving…' : 'Update password'}
                </button>
              </form>
              <p className="text-sm text-center mt-4" style={{ color: 'var(--muted)' }}>
                <Link to="/login" className="font-semibold" style={{ color: '#006080' }}>Back to sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
